import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Avatar,
  AvatarGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import SchoolIcon from "@mui/icons-material/School";
import GroupIcon from "@mui/icons-material/Group";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import StarIcon from "@mui/icons-material/Star";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PeopleIcon from "@mui/icons-material/People";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import color from "../../components/utils/Colors";
import { useSnackbar } from "../../components/utils/SnackBar";
import { useUser } from "../../services/Context/UserContext";
import { getDataFromTable, deleteDataFromTable } from "../../services/services";
import { CurrencyRupee } from "@mui/icons-material";

interface Course {
  id: string;
  title: string;
  studentsEnrolled: number;
  rating: number;
  reviews: number;
  revenue: number;
  status: "published" | "draft" | "archived";
  enrollmentTrend: number;
  completionRate: number;
  lastUpdated: string;
}

interface StudentReview {
  id: string;
  studentName: string;
  rating: number;
  review: string;
  course: string;
  date: string;
}

interface EarningsData {
  month: string;
  amount: number;
  students: number;
}

const isSuccessfulPayment = (status: string) =>
  status === "success" || status === "completed";

const parseReviewFeedback = (feedback?: string) => {
  let name = "Student";
  let text = feedback || "";
  try {
    if (feedback) {
      const parsed = JSON.parse(feedback);
      if (parsed && typeof parsed === "object") {
        name = parsed.authorName || "Student";
        text = parsed.text || "";
      }
    }
  } catch {
    // feedback is plain text
  }
  return { name, text };
};

const buildMonthlyEarnings = (payments: any[]): EarningsData[] => {
  const byMonth = new Map<
    string,
    { sortKey: number; month: string; amount: number; students: number }
  >();

  payments.forEach((payment) => {
    if (!isSuccessfulPayment(payment.status ?? "")) return;
    const date = payment.createdAt ? new Date(payment.createdAt) : new Date();
    const sortKey = date.getFullYear() * 12 + date.getMonth();
    const month = date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "long",
    });
    const key = String(sortKey);
    const existing = byMonth.get(key) ?? {
      sortKey,
      month,
      amount: 0,
      students: 0,
    };
    existing.amount += Number(payment.amount) || 0;
    existing.students += 1;
    byMonth.set(key, existing);
  });

  return Array.from(byMonth.values())
    .sort((a, b) => a.sortKey - b.sortKey)
    .map(({ month, amount, students }) => ({ month, amount, students }));
};

const TeacherDashboard: React.FC = () => {
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const isMobile = useMediaQuery("(max-width:600px)");
  const [currentTab, setCurrentTab] = useState(0);
  const [courses, setCourses] = useState<Course[]>([]);
  const [reviews, setReviews] = useState<StudentReview[]>([]);
  const [earnings, setEarnings] = useState<EarningsData[]>([]);
  const [createCourseDialogOpen, setCreateCourseDialogOpen] = useState(false);
  const [courseName, setCourseName] = useState("");

  const { user } = useUser();

  const fetchDashboardData = async () => {
    if (!user?.id) return;

    try {
      const response = await getDataFromTable("course", {
        createdBy: user.id,
      });
      const rawCourses: any[] = response?.data?.data ?? [];
      if (rawCourses.length === 0) {
        setCourses([]);
        setReviews([]);
        setEarnings([]);
        return;
      }

      const courseStats = await Promise.all(
        rawCourses.map(async (course) => {
          const courseId = course.id;
          const [enrolledRes, paymentsRes, ratingsRes] = await Promise.all([
            getDataFromTable("enrolledCourse", { courseId }),
            getDataFromTable("payments", { courseId }),
            getDataFromTable("userRatings", {
              feedbackType: `course_${courseId}`,
            }),
          ]);

          const enrollments: any[] = enrolledRes?.data?.data ?? [];
          const payments: any[] = paymentsRes?.data?.data ?? [];
          const ratings: any[] = ratingsRes?.data?.data ?? [];
          const successfulPayments = payments.filter((p) =>
            isSuccessfulPayment(p.status ?? ""),
          );
          const revenue = successfulPayments.reduce(
            (sum, p) => sum + (Number(p.amount) || 0),
            0,
          );
          const avgRating =
            ratings.length > 0
              ? ratings.reduce((sum, r) => sum + (Number(r.rating) || 0), 0) /
                ratings.length
              : 0;
          const completionRate =
            enrollments.length > 0
              ? Math.round(
                  enrollments.reduce(
                    (sum, e) => sum + (Number(e.progress) || 0),
                    0,
                  ) / enrollments.length,
                )
              : 0;

          return {
            course,
            enrollments,
            successfulPayments,
            ratings,
            revenue,
            avgRating,
            completionRate,
          };
        }),
      );

      const fetchedCourses: Course[] = courseStats.map(
        ({
          course,
          enrollments,
          ratings,
          revenue,
          avgRating,
          completionRate,
        }) => ({
          id: String(course.id),
          title: course.title || "Untitled Course",
          studentsEnrolled: enrollments.length,
          rating: Number(avgRating.toFixed(1)),
          reviews: ratings.length,
          revenue,
          status: course.status || "draft",
          enrollmentTrend: 0,
          completionRate,
          lastUpdated: course.updatedAt
            ? new Date(course.updatedAt).toLocaleDateString()
            : "N/A",
        }),
      );

      const allReviews: StudentReview[] = courseStats
        .flatMap(({ course, ratings }) =>
          ratings.map((item: any) => {
            const { name, text } = parseReviewFeedback(item.feedback);
            return {
              id: String(item.id),
              studentName: name,
              rating: Number(item.rating) || 0,
              review: text || "No comment provided.",
              course: course.title || "Untitled Course",
              date: item.createdAt
                ? new Date(item.createdAt).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })
                : "Recently",
              sortDate: item.createdAt ? new Date(item.createdAt).getTime() : 0,
            };
          }),
        )
        .sort((a, b) => (b as any).sortDate - (a as any).sortDate)
        .map(({ id, studentName, rating, review, course, date }) => ({
          id,
          studentName,
          rating,
          review,
          course,
          date,
        }));

      const allPayments = courseStats.flatMap(
        ({ successfulPayments }) => successfulPayments,
      );

      setCourses(fetchedCourses);
      setReviews(allReviews);
      setEarnings(buildMonthlyEarnings(allPayments));
    } catch (error) {
      console.error("Failed to fetch dashboard data", error);
      showSnackbar("Failed to fetch dashboard data", "error");
    }
  };

  useEffect(() => {
    fetchDashboardData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const handleEditCourse = (courseId: string) => {
    navigate("/course-desc", { state: { courseId } });
  };

  const handleViewAnalytics = (courseId: string) => {
    showSnackbar("Opening course analytics...");
  };

  // Stats
  const totalStudents = courses.reduce((sum, c) => sum + c.studentsEnrolled, 0);
  const totalEarnings = courses.reduce((sum, c) => sum + c.revenue, 0);
  const ratedCourses = courses.filter((c) => c.rating > 0);
  const avgRating =
    ratedCourses.length > 0
      ? (
          ratedCourses.reduce((sum, c) => sum + c.rating, 0) /
          ratedCourses.length
        ).toFixed(1)
      : "0.0";
  const publishedCourses = courses.filter(
    (c) => c.status === "published",
  ).length;

  const StatCard = ({
    icon: Icon,
    value,
    label,
    cardColor,
    trend,
  }: {
    icon: React.ComponentType<any>;
    value: string | number;
    label: string;
    cardColor: string;
    trend?: string;
  }) => (
    <Card
      sx={{
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
        borderRadius: "12px",
      }}
    >
      <CardContent sx={{ padding: "24px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "12px",
          }}
        >
          <Box
            sx={{
              background: cardColor,
              borderRadius: "50%",
              width: "50px",
              height: "50px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
            }}
          >
            <Icon sx={{ fontSize: "24px" }} />
          </Box>
          {trend && (
            <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
              <TrendingUpIcon sx={{ color: "#4caf50", fontSize: "16px" }} />
              <Typography
                variant="caption"
                sx={{ color: "#4caf50", fontWeight: "bold" }}
              >
                {trend}
              </Typography>
            </Box>
          )}
        </Box>
        <Typography
          variant="h4"
          sx={{
            color: color.textColor,
            fontWeight: "bold",
            marginBottom: "4px",
          }}
        >
          {label === "Total Earnings" ? <CurrencyRupee sx={{fontSize : 30}}/> : ""}
          {value}
        </Typography>
        <Typography variant="body2" sx={{ color: "#999" }}>
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: "100vh", background: "#f8f9fa", paddingY: "40px" }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box
          sx={{
            marginBottom: "40px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                color: color.textColor,
                fontWeight: "bold",
                marginBottom: "8px",
              }}
            >
              Dashboard
            </Typography>
            <Typography variant="body1" sx={{ color: "#666" }}>
              Manage your courses, track student progress, and grow your
              earnings
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              background: color.firstColor,
              color: "white",
              textTransform: "none",
              fontWeight: "bold",
              borderRadius: "8px",
              "&:hover": {
                background: color.forthColor,
              },
            }}
            onClick={() => navigate("/course-info")}
          >
            Create New Course
          </Button>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={3} sx={{ marginBottom: "40px" }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={SchoolIcon}
              value={publishedCourses}
              label="Published Courses"
              cardColor={color.firstColor}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={GroupIcon}
              value={totalStudents}
              label="Total Students"
              cardColor={color.thirdColor}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={AttachMoneyIcon}
              value={`${totalEarnings.toLocaleString()}`}
              label="Total Earnings"
              cardColor="#4caf50"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              icon={StarIcon}
              value={avgRating}
              label="Average Rating"
              cardColor="#ff9800"
            />
          </Grid>
        </Grid>

        {/* Tabs */}
        <Card
          sx={{
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
            borderRadius: "12px",
            overflow: "hidden",
          }}
        >
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: `2px solid ${color.secondColor}`,
              "& .MuiTab-root": {
                textTransform: "none",
                fontSize: "16px",
                fontWeight: "500",
                color: "#999",
                "&.Mui-selected": {
                  color: color.firstColor,
                  fontWeight: "bold",
                },
              },
            }}
          >
            <Tab label="My Courses" />
            <Tab label="Student Reviews" />
            <Tab label="Earnings" />
            <Tab label="Analytics" />
          </Tabs>

          {/* TAB 1: MY COURSES */}
          <TabPanel value={currentTab} index={0}>
            <Box sx={{ padding: "30px" }}>
              {courses.length > 0 ? (
                <Grid container spacing={3}>
                  {courses.map((course) => (
                    <Grid item xs={12} sm={6} md={4} key={course.id}>
                      <Card
                        sx={{
                          boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
                          borderRadius: "12px",
                          overflow: "hidden",
                          transition:
                            "transform 0.3s ease, box-shadow 0.3s ease",
                          "&:hover": {
                            transform: "translateY(-5px)",
                            boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.15)",
                          },
                        }}
                      >
                        <CardContent sx={{ padding: "20px" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "start",
                              marginBottom: "12px",
                            }}
                          >
                            <Typography
                              variant="h6"
                              sx={{
                                color: color.textColor,
                                fontWeight: "bold",
                                flex: 1,
                              }}
                            >
                              {course.title}
                            </Typography>
                            <Chip
                              label={
                                course.status === "published"
                                  ? "✓ Published"
                                  : course.status === "draft"
                                    ? "⚙ Draft"
                                    : "📦 Archived"
                              }
                              color={
                                course.status === "published"
                                  ? "success"
                                  : course.status === "draft"
                                    ? "warning"
                                    : "default"
                              }
                              size="small"
                              variant="outlined"
                            />
                          </Box>

                          <Box sx={{ marginBottom: "16px" }}>
                            {course.rating > 0 && (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginBottom: "8px",
                                }}
                              >
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  {[...Array(5)].map((_, i) => (
                                    <StarIcon
                                      key={i}
                                      sx={{
                                        fontSize: "16px",
                                        color:
                                          i < Math.floor(course.rating)
                                            ? "#ff9800"
                                            : "#ddd",
                                      }}
                                    />
                                  ))}
                                </Box>
                                <Typography
                                  variant="body2"
                                  sx={{ color: "#666" }}
                                >
                                  {course.rating} ({course.reviews} reviews)
                                </Typography>
                              </Box>
                            )}

                            <Box
                              sx={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: "12px",
                                marginBottom: "12px",
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  STUDENTS
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: color.firstColor,
                                    fontWeight: "bold",
                                  }}
                                >
                                  {course.studentsEnrolled}
                                </Typography>
                              </Box>
                              <Box>
                                <Typography
                                  variant="caption"
                                  sx={{ color: "#999", fontWeight: "bold" }}
                                >
                                  REVENUE
                                </Typography>
                                <Typography
                                  variant="h6"
                                  sx={{ color: "#4caf50", fontWeight: "bold" }}
                                >
                                  <CurrencyRupee sx={{fontSize:17}}/>{course.revenue}
                                </Typography>
                              </Box>
                            </Box>

                            {course.status === "published" && (
                              <Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    marginBottom: "4px",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{ color: "#999" }}
                                  >
                                    Enrollment Growth
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: color.firstColor,
                                      fontWeight: "bold",
                                    }}
                                  >
                                    +{course.enrollmentTrend}%
                                  </Typography>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={course.enrollmentTrend * 10}
                                  sx={{
                                    height: "6px",
                                    borderRadius: "3px",
                                    backgroundColor: "#e0e0e0",
                                    "& .MuiLinearProgress-bar": {
                                      backgroundColor: color.firstColor,
                                    },
                                  }}
                                />
                              </Box>
                            )}
                          </Box>

                          <Typography
                            variant="caption"
                            sx={{
                              color: "#999",
                              display: "block",
                              marginBottom: "12px",
                            }}
                          >
                            Last updated: {course.lastUpdated}
                          </Typography>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: "1fr 1fr",
                              gap: "8px",
                            }}
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              sx={{
                                borderColor: color.firstColor,
                                color: color.firstColor,
                                textTransform: "none",
                                "&:hover": {
                                  borderColor: color.forthColor,
                                  color: color.forthColor,
                                },
                              }}
                              onClick={() => handleEditCourse(course.id)}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AnalyticsIcon />}
                              sx={{
                                borderColor: color.thirdColor,
                                color: color.thirdColor,
                                textTransform: "none",
                                "&:hover": {
                                  borderColor: color.forthColor,
                                  color: color.forthColor,
                                },
                              }}
                              onClick={() => handleViewAnalytics(course.id)}
                            >
                              Analytics
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Box sx={{ textAlign: "center", padding: "60px 20px" }}>
                  <Typography sx={{ color: "#999", marginBottom: "20px" }}>
                    No courses yet. Create your first course to get started!
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{
                      background: color.firstColor,
                      textTransform: "none",
                      fontWeight: "bold",
                      color: "white",
                      "&:hover": {
                        background: color.forthColor,
                      },
                    }}
                    onClick={() => setCreateCourseDialogOpen(true)}
                  >
                    Create Your First Course
                  </Button>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* TAB 2: STUDENT REVIEWS */}
          <TabPanel value={currentTab} index={1}>
            <Box sx={{ padding: "30px" }}>
              {reviews.length > 0 ? (
                <Box
                  sx={{ display: "flex", flexDirection: "column", gap: "16px" }}
                >
                  {reviews.map((review) => (
                    <Paper
                      key={review.id}
                      sx={{ padding: "20px", borderRadius: "8px" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "start",
                          marginBottom: "12px",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="body2"
                            sx={{ color: color.textColor, fontWeight: "bold" }}
                          >
                            {review.studentName}
                          </Typography>
                          <Typography variant="caption" sx={{ color: "#999" }}>
                            {review.course}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                          }}
                        >
                          {[...Array(5)].map((_, i) => (
                            <StarIcon
                              key={i}
                              sx={{
                                fontSize: "16px",
                                color: i < review.rating ? "#ff9800" : "#ddd",
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "#666", marginBottom: "8px" }}
                      >
                        {review.review}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#999" }}>
                        {review.date}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              ) : (
                <Box sx={{ textAlign: "center", padding: "60px 20px" }}>
                  <Typography sx={{ color: "#999" }}>
                    No reviews yet. Once students complete your courses, their
                    reviews will appear here.
                  </Typography>
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* TAB 3: EARNINGS */}
          <TabPanel value={currentTab} index={2}>
            <Box sx={{ padding: "30px" }}>
              {earnings.length === 0 ? (
                <Box sx={{ textAlign: "center", padding: "60px 20px" }}>
                  <Typography sx={{ color: "#999" }}>
                    No earnings yet. Revenue will appear here when students
                    purchase your courses.
                  </Typography>
                </Box>
              ) : (
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <Typography
                      variant="h6"
                      sx={{
                        color: color.textColor,
                        fontWeight: "bold",
                        marginBottom: "20px",
                      }}
                    >
                      Monthly Earnings
                    </Typography>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ background: "#f5f5f5" }}>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: color.textColor,
                              }}
                            >
                              Month
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: color.textColor,
                                textAlign: "right",
                              }}
                            >
                              Earnings
                            </TableCell>
                            <TableCell
                              sx={{
                                fontWeight: "bold",
                                color: color.textColor,
                                textAlign: "right",
                              }}
                            >
                              Students
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {earnings.map((entry, index) => (
                            <TableRow
                              key={index}
                              sx={{ "&:hover": { background: "#f9f9f9" } }}
                            >
                              <TableCell>{entry.month}</TableCell>
                              <TableCell
                                sx={{
                                  textAlign: "right",
                                  fontWeight: "bold",
                                  color: "#4caf50",
                                }}
                              >
                                ${entry.amount.toLocaleString()}
                              </TableCell>
                              <TableCell sx={{ textAlign: "right" }}>
                                {entry.students}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Card
                      sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}
                    >
                      <CardContent>
                        <Typography
                          variant="h6"
                          sx={{
                            color: color.textColor,
                            fontWeight: "bold",
                            marginBottom: "20px",
                          }}
                        >
                          Earnings Summary
                        </Typography>

                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: "16px",
                          }}
                        >
                          <Box
                            sx={{
                              padding: "16px",
                              background: color.secondColor,
                              borderRadius: "8px",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              TOTAL EARNINGS
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                color: color.firstColor,
                                fontWeight: "bold",
                                marginTop: "8px",
                              }}
                            >
                              ${totalEarnings.toLocaleString()}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              padding: "16px",
                              background: color.secondColor,
                              borderRadius: "8px",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              AVAILABLE PAYOUT
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                color: "#4caf50",
                                fontWeight: "bold",
                                marginTop: "8px",
                              }}
                            >
                              ${(totalEarnings * 0.85).toLocaleString()}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              padding: "16px",
                              background: color.secondColor,
                              borderRadius: "8px",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              PENDING WITHDRAWAL
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                color: "#ff9800",
                                fontWeight: "bold",
                                marginTop: "8px",
                              }}
                            >
                              ${(totalEarnings * 0.15).toLocaleString()}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              padding: "16px",
                              background: color.secondColor,
                              borderRadius: "8px",
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{ color: "#999", fontWeight: "bold" }}
                            >
                              PLATFORM COMMISSION
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: color.textColor, marginTop: "8px" }}
                            >
                              15% (30 days hold)
                            </Typography>
                          </Box>
                        </Box>

                        <Button
                          fullWidth
                          variant="contained"
                          sx={{
                            background: color.firstColor,
                            color: "white",
                            marginTop: "20px",
                            textTransform: "none",
                            fontWeight: "bold",
                            "&:hover": {
                              background: color.forthColor,
                            },
                          }}
                        >
                          Request Payout
                        </Button>

                        <Button
                          fullWidth
                          variant="outlined"
                          sx={{
                            borderColor: color.firstColor,
                            color: color.firstColor,
                            marginTop: "12px",
                            textTransform: "none",
                            fontWeight: "bold",
                          }}
                        >
                          View Payment History
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}
            </Box>
          </TabPanel>

          {/* TAB 4: ANALYTICS */}
          <TabPanel value={currentTab} index={3}>
            <Box sx={{ padding: "30px" }}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Card sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{
                          color: color.textColor,
                          fontWeight: "bold",
                          marginBottom: "20px",
                        }}
                      >
                        Course Performance Overview
                      </Typography>

                      <TableContainer component={Paper}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ background: "#f5f5f5" }}>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  color: color.textColor,
                                }}
                              >
                                Course
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  color: color.textColor,
                                  textAlign: "center",
                                }}
                              >
                                Students
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  color: color.textColor,
                                  textAlign: "center",
                                }}
                              >
                                Completion Rate
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  color: color.textColor,
                                  textAlign: "center",
                                }}
                              >
                                Rating
                              </TableCell>
                              <TableCell
                                sx={{
                                  fontWeight: "bold",
                                  color: color.textColor,
                                  textAlign: "right",
                                }}
                              >
                                Revenue
                              </TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {courses
                              .filter((c) => c.status === "published")
                              .map((course) => (
                                <TableRow
                                  key={course.id}
                                  sx={{ "&:hover": { background: "#f9f9f9" } }}
                                >
                                  <TableCell
                                    sx={{
                                      fontWeight: "bold",
                                      color: color.textColor,
                                    }}
                                  >
                                    {course.title}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    {course.studentsEnrolled}
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      <LinearProgress
                                        variant="determinate"
                                        value={course.completionRate}
                                        sx={{
                                          width: "80px",
                                          height: "6px",
                                          borderRadius: "3px",
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{
                                          fontWeight: "bold",
                                          minWidth: "40px",
                                        }}
                                      >
                                        {course.completionRate}%
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell sx={{ textAlign: "center" }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: "4px",
                                      }}
                                    >
                                      <StarIcon
                                        sx={{
                                          color: "#ff9800",
                                          fontSize: "16px",
                                        }}
                                      />
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: "bold" }}
                                      >
                                        {course.rating}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      textAlign: "right",
                                      fontWeight: "bold",
                                      color: "#4caf50",
                                    }}
                                  >
                                    ${course.revenue.toLocaleString()}
                                  </TableCell>
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{
                          color: color.textColor,
                          fontWeight: "bold",
                          marginBottom: "20px",
                        }}
                      >
                        Student Demographics
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "16px",
                        }}
                      >
                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: "#999", fontWeight: "bold" }}
                          >
                            NEW STUDENTS (THIS MONTH)
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{
                              color: color.firstColor,
                              fontWeight: "bold",
                              marginTop: "8px",
                            }}
                          >
                            28
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: "#999", fontWeight: "bold" }}
                          >
                            ACTIVE STUDENTS
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{
                              color: color.thirdColor,
                              fontWeight: "bold",
                              marginTop: "8px",
                            }}
                          >
                            412
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: "#999", fontWeight: "bold" }}
                          >
                            COURSE COMPLETIONS
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{
                              color: "#4caf50",
                              fontWeight: "bold",
                              marginTop: "8px",
                            }}
                          >
                            156
                          </Typography>
                        </Box>

                        <Box>
                          <Typography
                            variant="caption"
                            sx={{ color: "#999", fontWeight: "bold" }}
                          >
                            DROPOUT RATE
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{
                              color: "#ff5252",
                              fontWeight: "bold",
                              marginTop: "8px",
                            }}
                          >
                            12%
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card sx={{ boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)" }}>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{
                          color: color.textColor,
                          fontWeight: "bold",
                          marginBottom: "20px",
                        }}
                      >
                        Top Performing Courses
                      </Typography>

                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "12px",
                        }}
                      >
                        {courses
                          .filter((c) => c.status === "published")
                          .sort((a, b) => b.rating - a.rating)
                          .slice(0, 3)
                          .map((course, index) => (
                            <Box
                              key={course.id}
                              sx={{
                                paddingBottom: "12px",
                                borderBottom: "1px solid #f0f0f0",
                                "&:last-child": { borderBottom: "none" },
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "12px",
                                  marginBottom: "8px",
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    color: color.firstColor,
                                    fontWeight: "bold",
                                    minWidth: "24px",
                                  }}
                                >
                                  #{index + 1}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: color.textColor,
                                    fontWeight: "bold",
                                    flex: 1,
                                  }}
                                >
                                  {course.title}
                                </Typography>
                              </Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "16px",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <StarIcon
                                    sx={{ color: "#ff9800", fontSize: "14px" }}
                                  />
                                  <Typography
                                    variant="caption"
                                    sx={{ fontWeight: "bold" }}
                                  >
                                    {course.rating}
                                  </Typography>
                                </Box>
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "4px",
                                  }}
                                >
                                  <GroupIcon
                                    sx={{
                                      color: color.thirdColor,
                                      fontSize: "14px",
                                    }}
                                  />
                                  <Typography variant="caption">
                                    {course.studentsEnrolled} students
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          ))}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          </TabPanel>
        </Card>
      </Container>
    </Box>
  );
};

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`teacher-tabpanel-${index}`}
      aria-labelledby={`teacher-tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

export default TeacherDashboard;
